import { useEffect, useState } from "react";
import { fetchData } from "../lib/dataCache";
import { formatTitle } from "../lib/formatText";

// "country" excluded, matching PlacesTab.js's existing use of this same endpoint -- not a
// meaningful geography level to search/group by here either.
const EXCLUDED_LEVELS = ["country"];

/**
 * Geography-level terms (State, County, Congressional District, ...) for the search bar --
 * GET /api/config/geography-levels via the same fetchData request cache PlacesTab.js already uses
 * to pair a place with a level, just consumed here as directly matchable/committable terms
 * (merged into useTermIndex) instead of a combobox's options.
 *
 * Each term's own `siblings` is the *other* geography levels, in full term shape (not just
 * {value, display} like a metric's or filter value's siblings) -- the per-type flow diagram's one
 * deliberate exception to how a token's ADD TO YOUR SEARCH list gets populated: a Geography Level
 * token shows these siblings directly, not generic typed-fragment matches (see
 * TokenizedSearchInput.js), so they need to already be commit-ready term objects.
 */
export function useGeographyLevels() {
  const [geographyLevels, setGeographyLevels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const body = await fetchData("/api/config/geography-levels");
        const levels = Object.keys(body.geography_levels).filter(
          (level) => !EXCLUDED_LEVELS.includes(level)
        );
        const terms = levels.map((level) => ({
          id: `geographyLevel:${level}`,
          display: formatTitle(level),
          category: "Geography Level",
          sourceType: "geographyLevel",
          feature: { type: "geographyLevel" },
          value: level,
        }));
        terms.forEach((term) => {
          term.siblings = terms.filter((other) => other.value !== term.value);
        });
        setGeographyLevels(terms);
      } catch (error) {
        console.error("Error fetching geography levels:", error);
        setGeographyLevels([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLevels();
  }, []);

  return { geographyLevels, isLoading };
}
